import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useCreateEndpointTest } from '@/hooks/useEndpointTests';
import { useToast } from '@/contexts/ToastContext';
import { useFlag } from '@/hooks/useFeatureFlags';

interface SaveAsTestDialogProps {
  endpointId: string;
  /** The input currently in the runner, captured when the dialog opened. */
  input: Record<string, unknown>;
  onClose: () => void;
}

/**
 * "Save as test" dialog (task 365): names the input currently in the runner
 * and saves it as a manual EndpointTest — the cheapest path from "useful
 * input" to "reusable test".
 */
export function SaveAsTestDialog(props: SaveAsTestDialogProps) {
  // Save as scenario answers to the test runs lever (task 1215); its trigger is gated too.
  const runsEnabled = useFlag('innovate.fe.testing.runs');
  return runsEnabled ? <SaveAsTestDialogContent {...props} /> : null;
}

function SaveAsTestDialogContent({ endpointId, input, onClose }: SaveAsTestDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const createTest = useCreateEndpointTest();
  const toast = useToast();

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await createTest.mutateAsync({
        endpointId,
        name: trimmed,
        ...(description.trim() ? { description: description.trim() } : {}),
        input,
        source: 'manual',
      });
      toast.success('Scenario saved', trimmed);
      onClose();
    } catch (err) {
      toast.error('Failed to save scenario', err instanceof Error ? err.message : undefined);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-as-test-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-start justify-between mb-4">
          <h3 id="save-as-test-title" className="text-lg font-medium text-gray-900 dark:text-white">
            Save as scenario
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="save-as-test-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name
            </label>
            <input
              id="save-as-test-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Typical three-message thread"
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="save-as-test-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              id="save-as-test-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Input</span>
            <pre className="p-3 bg-gray-50 dark:bg-gray-900 rounded text-xs font-mono text-gray-800 dark:text-gray-200 overflow-auto max-h-40">
              {JSON.stringify(input, null, 2)}
            </pre>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={createTest.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!name.trim() || createTest.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createTest.isPending ? 'Saving…' : 'Save scenario'}
          </button>
        </div>
      </div>
    </div>
  );
}
